import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Surrey = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E7%A7%9F%E6%88%BF%E5%AE%9D%E5%85%B8-%E5%A4%A7%E6%B8%A9%E5%93%A5%E5%8D%8E%E7%A7%9F%E9%87%91%E6%9C%80%E4%BE%BF%E5%AE%9C%E7%9A%8410%E4%B8%AA%E5%9C%B0%E5%8C%BA/'
    : 'https://liv.rent/blog/neighbourhoods/neighbourhood-guide-surrey/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.surrey:About Surrey Paragraph 1">
            The second-largest city in{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia
            </LinkText>
            , Surrey is a diverse community that has become a destination for
            individuals, families and businesses alike. It is well serviced by
            transit and boasts a number of vibrant business centres, shopping
            malls, recreational areas and green spaces. Housing options range
            from small studio apartments by Surrey City Centre and King George
            Skytrain stations to upscale homes in the Panorama Ridge ‘hood.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.surrey:About Surrey Paragraph 2">
            Surrey is also the fastest growing city in Metro Vancouver, which is
            not too surprising considering that Surrey consistently offers the
            lowest average rent prices in the region. If you are looking for an
            affordable apartment or home to rent without sacrificing all the
            world-class amenities that the Greater Vancouver area offers, Surrey
            might be the right choice for you.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,600 - $2,200',
          bed2: '$2,200 - $2,800',
          bed3: '$3,000 - $3,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.surrey:Apartment Hunting in Surrey Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s fairly easy to find relatively{' '}
            <LinkText href={links.under1000}>cheap rentals in Surrey</LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to this
            guide on{' '}
            <LinkText href={neighborhoodsLink}>Surrey neighbourhoods</LinkText>.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.surrey:Apartments for rent Surrey Paragraph">
              Because it’s such a densely-populated urban area, apartments are
              the most common form of housing you’ll find in Surrey. The city
              has become a popular destination for renters as{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Surrey
              </LinkText>{' '}
              are comparatively cheaper than nearby cities, even compared to
              other Canadian provinces.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.surrey:Houses for rent Surrey Paragraph">
              Outside of the downtown area, there are plenty of{' '}
              <LinkText href={links.house}>houses for rent in Surrey</LinkText>.
              Both partial houses and entire houses for rent are common,
              including both new and old homes, often with large yards.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.surrey:Condos for rent Surrey Paragraph">
              In more urban areas like Downtown Surrey,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Surrey
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Paragraph">
              {{ cityName }} isn’t known for inexpensive rent, but if you’re
              careful in your search, it shouldn’t be too hard to find something
              within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.surrey:Living in Surrey Paragraph">
          <Paragraph>
            Despite its surprisingly cheap rent prices, Surrey has everything
            today’s renters could ask for, from large shopping malls to outdoor
            and indoor recreational facilities. With an incredibly diverse
            population and no shortage of things to do, renters should keep this
            city in mind when deciding where to move in British Columbia. The
            amount of options for{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/what-can-1800-in-rent-get-you-in-metro-vancouver/">
              places to rent under $1,800
            </LinkText>{' '}
            is also much larger in Surrey than in neighbouring cities.
          </Paragraph>
          <Paragraph>
            If you’re not sure that Surrey is the right city for you, check out
            some of these resources to compare Surrey with other Canadian
            cities.
          </Paragraph>
        </Trans>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e6%b8%a9%e5%93%a5%e5%8d%8e%e5%92%8c%e7%b4%a0%e9%87%8c%e5%b1%85%e4%bd%8f%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              素里与温哥华：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/surrey-vs-vancouver-bc-comparison/">
              Surrey vs Vancouver: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/surrey-or-toronto-a-cost-of-living-comparison/">
              Surrey vs Toronto: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.surrey:Popular Neighbourhoods Surrey Paragraph">
          Popular neighbourhoods in Surrey include Whalley, Newton, Fleetwood,
          Bolivar, Clayton, Cloverdale, Guildford, Green Timbers, Fraser
          Heights, and South Surrey. For more about Surrey’s individual
          neighbourhoods, you can consult this guide on{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-surrey/">
            Surrey neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.surrey:Local Landmarks Surrey Paragraph">
          Surrey is a haven for lovers of the outdoors, offering up an abundant
          choice of parks and nature reserves, without sacrificing the amenities
          of city life. Some of Surrey’s most popular attractions and landmarks
          are Crescent Beach, the Peace Arch Provincial Park, Historic Stewart
          Farm, Central City Shopping Centre, Green Timbers Urban Forest,
          Cloverdale Town Centre, Bear Creek Park, Port Mann Bridge, Fraser
          Downs Racetrack & Casino, Darts Hill Garden Park, and the Barnston
          Island Ferry.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Surrey
